import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { FetchBikeThreewheelSalesReport_api, FetchDealers_api } from "../../Api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Search, Download, Calendar, Truck, User, Bike, Store, Wallet, Zap } from "lucide-react";
import { Spin, Select, DatePicker, ConfigProvider } from "antd";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";

const { Option } = Select;
const { RangePicker } = DatePicker;

export const SalesReportBikeThreewheel = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.REPORT_BIKE_THREEWHEEL,
    ["admin", "manager"],
  );

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dealerId, setDealerId] = useState<string>("");
  const [vehicleType, setVehicleType] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [dealers, setDealers] = useState<any[]>([]);
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDealers, setFetchingDealers] = useState(false);

  useEffect(() => {
    if (hasPermission) {
      loadDealers();
    }
  }, [hasPermission]);

  const loadDealers = async () => {
    setFetchingDealers(true);
    const response = await FetchDealers_api(1, 100, "");
    if (response && response.isDataSet) {
      setDealers(response.dealerData || []);
    }
    setFetchingDealers(false);
  };

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    const response = await FetchBikeThreewheelSalesReport_api(
      startDate,
      endDate,
      dealerId,
      vehicleType,
      paymentStatus,
    );
    if (response && response.success) {
      setReportData(response.data || []);
    } else {
      alert(response?.msg || "Failed to fetch report");
    }
    setLoading(false);
  };

  const toNumber = (value: any): number =>
    Number(String(value ?? "0").replace(/,/g, "")) || 0;

  const calculateTotal = () => {
    const total = (reportData || []).reduce(
      (acc, curr) => acc + toNumber(curr.amount || 0),
      0,
    );
    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(total);
  };

  const handleDownloadExcel = () => {
    const selectedDealerName =
      (dealers || []).find((d) => d.id == dealerId)?.name || "All Dealers";
    const selectedTypeLabel = vehicleType || "Bikes & Threewheels";
    
    const headers = [
      "Date",
      "Vehicle",
      "Type",
      "Dealer",
      "Customer",
      "Amount",
    ];
    const data = reportData.map((row) => [
      row.date || "N/A",
      `${row.vehicle_model} (${row.engine_number})`,
      row.vehicle_type,
      row.dealer_name,
      `${row.customer_name} (${row.customer_mobile})`,
      toNumber(row.amount || 0),
    ]);
    downloadExcel(
      `${selectedTypeLabel} Sales Report - ${selectedDealerName} (${startDate} to ${endDate})`,
      headers,
      data,
      `Sales_Report_${startDate}_${endDate}`,
      undefined,
      undefined,
      calculateTotal(),
      `${reportData.length} Units`,
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Vehicle Sales Report</h1>
        <div className="flex gap-3">
          <Button 
            onClick={handleDownloadExcel} 
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end text-sm">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Authorized Dealer
          </label>
          <Select
            className="w-full h-[38px]"
            placeholder="Select Dealer"
            value={dealerId}
            onChange={(value) => setDealerId(value)}
            loading={fetchingDealers}
            showSearch
            optionFilterProp="children"
          >
            <Option value="">All Dealers</Option>
            {dealers?.map((d) => (
              <Option key={d.id} value={d.id}>
                {d.name}
              </Option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Category
          </label>
          <Select
            className="w-full h-[38px]"
            placeholder="Select Type"
            value={vehicleType}
            onChange={(value) => setVehicleType(value)}
          >
            <Option value="">All Categories</Option>
            <Option value="Bike">Bikes</Option>
            <Option value="Three Wheel">Three Wheels</Option>
          </Select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Status
          </label>
          <Select
            className="w-full h-[38px]"
            placeholder="Select Status"
            value={paymentStatus}
            onChange={(value) => setPaymentStatus(value)}
          >
            <Option value="">All Statuses</Option>
            <Option value="Paid">Fully Paid</Option>
            <Option value="pending">Pending Settlement</Option>
          </Select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Start Date
          </label>
          <DatePicker 
            className="w-full h-[38px]"
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => setStartDate(date ? date.format("YYYY-MM-DD") : "")}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            End Date
          </label>
          <DatePicker 
            className="w-full h-[38px]"
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => setEndDate(date ? date.format("YYYY-MM-DD") : "")}
          />
        </div>

        <div className="flex justify-end">
          <Button
            onClick={fetchReport}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full h-[38px]"
          >
            {loading ? <Spin size="small" /> : <Search className="w-4 h-4 mr-2" />}
            <span>Search</span>
          </Button>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL TRANSACTION VALUE</p>
                <p className="text-2xl font-bold">{calculateTotal()}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">UNITS SOLD</p>
                <p className="text-2xl font-bold">{reportData.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col pt-6">
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <Table className="min-w-max text-sm">
                <TableHeader className="bg-gray-100/50">
                  <TableRow className="border-b">
                    <TableHead className="py-3 px-4 font-semibold text-gray-700">Reference</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700">Asset Profile</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700">Logistics Node</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700">Customer / Entity</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Valuation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-20">
                          <Spin size="large" />
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.map((row, index) => (
                        <TableRow 
                          key={`${row.engine_number}-${index}`}
                          className="border-b last:border-0 hover:bg-gray-50"
                        >
                          <TableCell className="px-4 py-3">
                            <div className="flex flex-col">
                               <span className="font-medium text-gray-900">{row.date || "N/A"}</span>
                               <span className="text-xs text-gray-500">STK-{index + 1000}</span>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                             <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                   <span className="font-medium text-gray-800">{row.vehicle_model}</span>
                                   <span className={`px-2 py-0.5 rounded text-[10px] uppercase border ${row.vehicle_type === 'Bike' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-purple-50 text-purple-600 border-purple-100'}`}>
                                      {row.vehicle_type}
                                   </span>
                                </div>
                                <span className="text-xs text-gray-500 mt-0.5">E: {row.engine_number}</span>
                             </div>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                             <div className="flex flex-col">
                                <span className="text-sm text-gray-700">{row.dealer_name}</span>
                                <span className={`text-[10px] uppercase mt-1 inline-flex w-fit px-2 py-0.5 rounded border ${row.status === 'Paid' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                                   {row.status || 'Pending'}
                                </span>
                             </div>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-700">{row.customer_name}</span>
                              <span className="text-xs text-gray-500">{row.customer_mobile}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right px-4 py-3 font-medium text-gray-900">
                             {new Intl.NumberFormat("en-LK", {
                                style: "currency",
                                currency: "LKR",
                                minimumFractionDigits: 2,
                              }).format(toNumber(row.amount || 0))}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {reportData.length === 0 && !loading && (startDate || endDate) && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No sales activity recorded for this criteria.</p>
          </div>
        </div>
      )}
      {reportData.length === 0 && !loading && !startDate && !endDate && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Define search parameters to view sales insights.</p>
          </div>
        </div>
      )}
    </div>
  );
};
